import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  createAdminRequestType,
  type DepartmentAdminResponse,
  type RequestTypeCreatePayload,
} from "../../api/admin";
import "./RequestCategoryCreatePanel.css";

export type RequestCategoryCreatePanelProps = {
  departments: ReadonlyArray<DepartmentAdminResponse>;
  disabled: boolean;
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: () => Promise<void>;
  onCreateOpen?: () => void;
};

type FieldErrors = {
  code: string;
  name: string;
  sortOrder: string;
};

type FormState = {
  code: string;
  name: string;
  description: string;
  sortOrder: string;
  defaultDepartmentId: number | null;
  isInternalVisible: boolean;
  isCustomerVisible: boolean;
  isPartnerVisible: boolean;
};

type UiState = {
  isSubmitting: boolean;
  fieldErrors: FieldErrors;
  formError: string;
};

const INITIAL_FORM: FormState = {
  code: "",
  name: "",
  description: "",
  sortOrder: "0",
  defaultDepartmentId: null,
  isInternalVisible: true,
  isCustomerVisible: false,
  isPartnerVisible: false,
};

const INITIAL_UI: UiState = {
  isSubmitting: false,
  fieldErrors: { code: "", name: "", sortOrder: "" },
  formError: "",
};

const FRIENDLY_FIELD_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  INVALID_CODE:
    "Code must start with a capital letter and contain only capital letters, numbers, or underscores.",
};

function friendlyFieldErrorMessage(
  errorCode: string | null,
  fallback: string,
): string {
  if (errorCode === null) {
    return fallback;
  }
  const mapped = FRIENDLY_FIELD_ERROR_MESSAGES[errorCode];
  return mapped ?? fallback;
}

function resolveFieldNameFromLocation(
  location: ReadonlyArray<string | number>,
): string | null {
  for (let index = location.length - 1; index >= 0; index -= 1) {
    const segment = location[index];
    if (typeof segment === "string" && segment.length > 0) {
      return segment;
    }
  }
  return null;
}

function describeRoutingDepartment(
  department: DepartmentAdminResponse,
): string {
  const companyName = department.company_name ?? "Unassigned";
  return companyName + " - " + department.name;
}

export default function RequestCategoryCreatePanel(
  props: RequestCategoryCreatePanelProps,
) {
  const { departments, disabled, isOpen, onOpenChange, onCreated, onCreateOpen } = props;
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [ui, setUi] = useState<UiState>(INITIAL_UI);
  const headingId = useId();
  const codeErrorId = useId();
  const nameErrorId = useId();
  const sortErrorId = useId();
  const departmentErrorId = useId();
  const audienceErrorId = useId();
  const formErrorId = useId();
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const isMountedRef = useRef(true);
  const shouldReturnFocusRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const eligibleDepartments = departments;

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      fieldErrors: { code: "", name: "", sortOrder: "" },
      formError: "",
    }));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const noDepartmentsAvailable = eligibleDepartments.length === 0;

  const openForm = useCallback(() => {
    if (disabled || ui.isSubmitting || noDepartmentsAvailable || isOpen) {
      return;
    }
    onCreateOpen?.();
    setForm({
      ...INITIAL_FORM,
      defaultDepartmentId: eligibleDepartments[0]?.id ?? null,
    });
    setUi({ ...INITIAL_UI });
    onOpenChange(true);
  }, [
    disabled,
    eligibleDepartments,
    isOpen,
    noDepartmentsAvailable,
    onCreateOpen,
    onOpenChange,
    ui.isSubmitting,
  ]);

  const closeForm = useCallback(() => {
    if (!isOpen) {
      return;
    }
    setForm(INITIAL_FORM);
    setUi(INITIAL_UI);
    onOpenChange(false);
    shouldReturnFocusRef.current = true;
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (isOpen || !shouldReturnFocusRef.current) {
      return;
    }
    if (noDepartmentsAvailable) {
      shouldReturnFocusRef.current = false;
      return;
    }
    if (disabled) {
      return;
    }
    shouldReturnFocusRef.current = false;
    addButtonRef.current?.focus();
  }, [disabled, isOpen, noDepartmentsAvailable]);

  useEffect(() => {
    if (isOpen) {
      headingRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setForm((prev) => {
      if (
        prev.defaultDepartmentId !== null &&
        eligibleDepartments.some(
          (department) => department.id === prev.defaultDepartmentId,
        )
      ) {
        return prev;
      }
      return {
        ...prev,
        defaultDepartmentId: eligibleDepartments[0]?.id ?? null,
      };
    });
  }, [eligibleDepartments, isOpen]);

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, code: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, code: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, name: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, name: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, description: nextValue }));
      setUi((prev) => ({
        ...prev,
        formError: "",
      }));
    },
    [],
  );

  const handleSortOrderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, sortOrder: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, sortOrder: "" },
        formError: "",
      }));
    },
    [],
  );

  const handleDepartmentChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const rawValue = event.target.value;
      const parsed = rawValue === "" ? null : Number.parseInt(rawValue, 10);
      const nextValue =
        parsed !== null && Number.isFinite(parsed) ? parsed : null;
      setForm((prev) => ({ ...prev, defaultDepartmentId: nextValue }));
      setUi((prev) => ({
        ...prev,
        formError: "",
      }));
    },
    [],
  );

  const handleInternalToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isInternalVisible: !prev.isInternalVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "" }));
  }, []);

  const handleCustomerToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isCustomerVisible: !prev.isCustomerVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "" }));
  }, []);

  const handlePartnerToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isPartnerVisible: !prev.isPartnerVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "" }));
  }, []);

  const buildClientFieldErrors = useCallback((): FieldErrors => {
    const trimmedCode = form.code.trim();
    const trimmedName = form.name.trim();
    const trimmedSort = form.sortOrder.trim();
    const sortAsNumber =
      trimmedSort.length === 0 ? Number.NaN : Number(trimmedSort);
    const sortIsValidInteger =
      trimmedSort.length > 0 &&
      Number.isFinite(sortAsNumber) &&
      Number.isInteger(sortAsNumber) &&
      sortAsNumber >= 0;
    const fieldErrors: FieldErrors = {
      code: trimmedCode.length === 0 ? "Please provide a category code." : "",
      name: trimmedName.length === 0 ? "Please provide a category name." : "",
      sortOrder: sortIsValidInteger
        ? ""
        : "Sort order must be a non-negative whole number.",
    };
    return fieldErrors;
  }, [form.code, form.name, form.sortOrder]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled || noDepartmentsAvailable) {
        return;
      }
      clearNotifications();

      const clientErrors = buildClientFieldErrors();
      const hasClientErrors =
        clientErrors.code.length > 0 ||
        clientErrors.name.length > 0 ||
        clientErrors.sortOrder.length > 0;
      if (hasClientErrors) {
        setUi((prev) => ({
          ...prev,
          fieldErrors: clientErrors,
        }));
        return;
      }

      if (form.defaultDepartmentId === null) {
        setUi((prev) => ({
          ...prev,
          formError: "Please choose a routing department.",
        }));
        return;
      }

      const trimmedDescription = form.description.trim();
      const sortAsNumber = Number(form.sortOrder.trim());

      const payload: RequestTypeCreatePayload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: trimmedDescription.length === 0 ? null : trimmedDescription,
        sort_order: sortAsNumber,
        default_department_id: form.defaultDepartmentId,
        is_internal_visible: form.isInternalVisible,
        is_customer_visible: form.isCustomerVisible,
        is_partner_visible: form.isPartnerVisible,
      };

      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));

      try {
        await createAdminRequestType(payload);
        if (!isMountedRef.current) {
          return;
        }
        setForm(INITIAL_FORM);
        setUi({ ...INITIAL_UI });
        shouldReturnFocusRef.current = true;
        await onCreated();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          const fieldErrors: FieldErrors = {
            code: "",
            name: "",
            sortOrder: "",
          };
          let hasRecognisedFieldError = false;
          let hasUnrecognisedFieldError = false;
          for (const fieldError of error.fieldErrors) {
            const fieldName = resolveFieldNameFromLocation(fieldError.location);
            if (fieldName === null) {
              hasUnrecognisedFieldError = true;
              continue;
            }
            if (fieldName === "code") {
              fieldErrors.code = friendlyFieldErrorMessage(
                error.errorCode,
                fieldError.message,
              );
              hasRecognisedFieldError = true;
            } else if (fieldName === "name") {
              fieldErrors.name = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "sort_order") {
              fieldErrors.sortOrder = fieldError.message;
              hasRecognisedFieldError = true;
            } else {
              hasUnrecognisedFieldError = true;
            }
          }
          if (error.errorCode === "INVALID_CODE") {
            fieldErrors.code =
              "Code must start with a capital letter and contain only capital letters, numbers, or underscores.";
            hasRecognisedFieldError = true;
            hasUnrecognisedFieldError = false;
          } else if (error.errorCode === "DUPLICATE_REQUEST_TYPE_CODE") {
            fieldErrors.code = "Request category code already exists.";
            hasRecognisedFieldError = true;
            hasUnrecognisedFieldError = false;
          } else if (error.errorCode === "DUPLICATE_REQUEST_TYPE_NAME") {
            fieldErrors.name = "Request category name already exists.";
            hasRecognisedFieldError = true;
            hasUnrecognisedFieldError = false;
          }
          const showDetail =
            !hasRecognisedFieldError || hasUnrecognisedFieldError;
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            fieldErrors,
            formError: showDetail
              ? friendlyFieldErrorMessage(error.errorCode, error.detail)
              : "",
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError:
            "Unable to create the request category. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [
      buildClientFieldErrors,
      clearNotifications,
      disabled,
      form.code,
      form.defaultDepartmentId,
      form.description,
      form.isCustomerVisible,
      form.isInternalVisible,
      form.isPartnerVisible,
      form.name,
      form.sortOrder,
      noDepartmentsAvailable,
      onCreated,
    ],
  );

  const allInputsDisabled =
    disabled || ui.isSubmitting || noDepartmentsAvailable;
  const hasAudienceSelected =
    form.isInternalVisible ||
    form.isCustomerVisible ||
    form.isPartnerVisible;

  return (
    <div className="request-category-create-panel">
      <button
        type="button"
        ref={addButtonRef}
        className="request-category-create-panel__add"
        onClick={openForm}
        disabled={allInputsDisabled}
      >
        Add Request Category
      </button>
      {noDepartmentsAvailable ? (
        <p
          id={departmentErrorId}
          className="request-category-create-panel__no-departments"
          role="status"
        >
          No active routing-enabled Internal-company Departments are currently
          configured. Request Categories cannot be created until at least one
          eligible Department exists.
        </p>
      ) : null}
      {isOpen ? (
        <form
          noValidate
          className="request-category-create-panel__form"
          onSubmit={handleSubmit}
          aria-labelledby={headingId}
        >
          <h2
            id={headingId}
            className="request-category-create-panel__title"
            tabIndex={-1}
            ref={headingRef}
          >
            Create Request Category
          </h2>
          <div className="request-category-create-panel__field">
            <label
              htmlFor="request-category-create-code"
              className="request-category-create-panel__label"
            >
              Category code
            </label>
            <input
              id="request-category-create-code"
              className="request-category-create-panel__input"
              type="text"
              value={form.code}
              onChange={handleCodeChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.code.length > 0}
              aria-describedby={
                ui.fieldErrors.code.length > 0 ? codeErrorId : undefined
              }
            />
            {ui.fieldErrors.code.length > 0 ? (
              <p
                id={codeErrorId}
                className="request-category-create-panel__field-error"
              >
                {ui.fieldErrors.code}
              </p>
            ) : null}
          </div>

          <div className="request-category-create-panel__field">
            <label
              htmlFor="request-category-create-name"
              className="request-category-create-panel__label"
            >
              Category name
            </label>
            <input
              id="request-category-create-name"
              className="request-category-create-panel__input"
              type="text"
              value={form.name}
              onChange={handleNameChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.name.length > 0}
              aria-describedby={
                ui.fieldErrors.name.length > 0 ? nameErrorId : undefined
              }
            />
            {ui.fieldErrors.name.length > 0 ? (
              <p
                id={nameErrorId}
                className="request-category-create-panel__field-error"
              >
                {ui.fieldErrors.name}
              </p>
            ) : null}
          </div>

          <div className="request-category-create-panel__field">
            <label
              htmlFor="request-category-create-description"
              className="request-category-create-panel__label"
            >
              Description
            </label>
            <textarea
              id="request-category-create-description"
              className="request-category-create-panel__textarea"
              value={form.description}
              onChange={handleDescriptionChange}
              disabled={allInputsDisabled}
              rows={3}
            />
          </div>

          <div className="request-category-create-panel__field">
            <label
              htmlFor="request-category-create-sort-order"
              className="request-category-create-panel__label"
            >
              Sort order
            </label>
            <input
              id="request-category-create-sort-order"
              className="request-category-create-panel__input"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={form.sortOrder}
              onChange={handleSortOrderChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
              aria-invalid={ui.fieldErrors.sortOrder.length > 0}
              aria-describedby={
                ui.fieldErrors.sortOrder.length > 0 ? sortErrorId : undefined
              }
            />
            {ui.fieldErrors.sortOrder.length > 0 ? (
              <p
                id={sortErrorId}
                className="request-category-create-panel__field-error"
              >
                {ui.fieldErrors.sortOrder}
              </p>
            ) : null}
          </div>

          <div className="request-category-create-panel__field">
            <label
              htmlFor="request-category-create-default-department"
              className="request-category-create-panel__label"
            >
              Default routing Department
            </label>
            <select
              id="request-category-create-default-department"
              className="request-category-create-panel__select"
              value={
                form.defaultDepartmentId === null
                  ? ""
                  : String(form.defaultDepartmentId)
              }
              onChange={handleDepartmentChange}
              disabled={allInputsDisabled}
              required
              aria-required="true"
            >
              {eligibleDepartments.map((department) => (
                <option key={department.id} value={department.id}>
                  {describeRoutingDepartment(department)}
                </option>
              ))}
            </select>
            <p className="request-category-create-panel__hint">
              Only active, routing-enabled Departments owned by an Internal
              company are eligible.
            </p>
          </div>

          <fieldset
            className="request-category-create-panel__audience"
            aria-describedby={
              hasAudienceSelected ? undefined : audienceErrorId
            }
          >
            <legend className="request-category-create-panel__label">
              Audience visibility
            </legend>
            <label className="request-category-create-panel__audience-option">
              <input
                type="checkbox"
                checked={form.isInternalVisible}
                onChange={handleInternalToggle}
                disabled={allInputsDisabled}
              />
              <span>Internal requesters</span>
            </label>
            <label className="request-category-create-panel__audience-option">
              <input
                type="checkbox"
                checked={form.isCustomerVisible}
                onChange={handleCustomerToggle}
                disabled={allInputsDisabled}
              />
              <span>Customer requesters</span>
            </label>
            <label className="request-category-create-panel__audience-option">
              <input
                type="checkbox"
                checked={form.isPartnerVisible}
                onChange={handlePartnerToggle}
                disabled={allInputsDisabled}
              />
              <span>Partner requesters</span>
            </label>
            {!hasAudienceSelected ? (
              <p
                id={audienceErrorId}
                className="request-category-create-panel__field-error"
              >
                At least one audience must be enabled.
              </p>
            ) : null}
          </fieldset>

          {ui.formError.length > 0 ? (
            <p
              id={formErrorId}
              className="request-category-create-panel__form-error"
              role="alert"
            >
              {ui.formError}
            </p>
          ) : null}

          <div
            className="request-category-create-panel__actions"
            role="group"
            aria-labelledby={headingId}
          >
            <button
              type="submit"
              className="request-category-create-panel__submit"
              disabled={
                allInputsDisabled || !hasAudienceSelected
              }
              aria-busy={ui.isSubmitting}
            >
              {ui.isSubmitting ? "Creating…" : "Create Request Category"}
            </button>
            <button
              type="button"
              className="request-category-create-panel__cancel"
              onClick={closeForm}
              disabled={ui.isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}